import { Clock, Download, Play, RotateCcw } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import type { ExamBooklet } from '../../../types/practice-exam';

// Oturum süresi tarayıcıda tutulur: sayfa yenilense de sürer. Süre bilgilendirir, süre bitince deneme kapanmaz.
const timerKey = (examId: string, bookletKey: string) => `examBookletTimer:${examId}:${bookletKey}`;

const readTimer = (key: string) => {
  try {
    const value = Number(window.localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
};

const writeTimer = (key: string, value: number | null) => {
  try {
    if (value) window.localStorage.setItem(key, String(value));
    else window.localStorage.removeItem(key);
  } catch {
    // Gizli sekme vb.: sayaç yalnız bu oturumda çalışır
  }
};

const formatRemaining = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const BookletTimer = ({ examId, booklet }: { examId: string; booklet: Pick<ExamBooklet, 'key' | 'durationMinutes'> }) => {
  const key = timerKey(examId, booklet.key);
  const [startedAt, setStartedAt] = useState<number | null>(() => readTimer(key));
  const [now, setNow] = useState(() => Date.now());
  const durationMs = (booklet.durationMinutes || 0) * 60000;

  useEffect(() => {
    setStartedAt(readTimer(key));
  }, [key]);

  useEffect(() => {
    if (!startedAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  if (!durationMs) return null;

  if (!startedAt) {
    return (
      <button
        type="button"
        onClick={() => {
          const value = Date.now();
          writeTimer(key, value);
          setNow(value);
          setStartedAt(value);
        }}
        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700"
      >
        <Play size={13} /> Süreyi başlat ({booklet.durationMinutes} dk)
      </button>
    );
  }

  const remaining = durationMs - (now - startedAt);
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold ${remaining > 0 ? 'bg-blue-50 text-blue-800' : 'bg-red-50 text-red-700'}`}>
        <Clock size={13} /> {remaining > 0 ? formatRemaining(remaining) : 'Süre doldu'}
      </span>
      <button
        type="button"
        title="Süreyi sıfırla"
        onClick={() => {
          writeTimer(key, null);
          setStartedAt(null);
        }}
        className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"
      >
        <RotateCcw size={13} />
      </button>
    </span>
  );
};

export default function ExamBookletViewer({ examId, booklets }: { examId: string; booklets: ExamBooklet[] }) {
  const [activeKey, setActiveKey] = useState(booklets[0]?.key);
  const active = booklets.find((booklet) => booklet.key === activeKey) || booklets[0];
  if (!active) return null;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-2">
        {booklets.map((booklet) => (
          <button
            key={booklet.key}
            type="button"
            onClick={() => setActiveKey(booklet.key)}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${booklet.key === active.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {booklet.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <BookletTimer examId={examId} booklet={active} />
          {active.pdfUrl && (
            <a
              href={active.pdfUrl}
              target="_blank"
              rel="noreferrer"
              title="Kitapçığı PDF olarak indir"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              <Download size={13} /> PDF
            </a>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-gray-100 p-2">
        {Array.from({ length: active.pageCount }, (_, index) => (
          <img
            key={`${active.key}-${index}`}
            src={`${active.pageBaseUrl}${index + 1}.jpg`}
            alt={`${active.label} sayfa ${index + 1}`}
            loading="lazy"
            className="mx-auto w-full max-w-3xl rounded bg-white shadow-sm"
          />
        ))}
      </div>
    </div>
  );
}
